/* ============================================================
   Collection-scoped search
   ------------------------------------------------------------
   Adds a search box to the collection page that only searches
   products inside the collection being viewed (plus any of its
   sub-collections). Matches on title, vendor, product type and
   tags, tolerates typos, and understands "Class 10" == "Class X".

   Results appear two ways at once:
     1. a dropdown of the closest matches (click = go to product)
     2. the product grid below filters live to every match

   The index is built from the storefront's public
   /collections/<handle>/products.json feed, so it is not limited
   to the 24 products rendered on the current page.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-collection-search]');
  if (!root) return;

  var input = root.querySelector('[data-coll-search-input]');
  var panel = root.querySelector('[data-coll-search-panel]');
  var clearBtn = root.querySelector('[data-coll-search-clear]');
  var status = document.querySelector('[data-coll-search-status]');
  var statusText = document.querySelector('[data-coll-search-status-text]');
  var resetBtn = document.querySelector('[data-coll-search-reset]');
  var grid = document.querySelector('[data-coll-grid]');
  var results = document.querySelector('[data-coll-results]');
  var empty = document.querySelector('[data-coll-empty]');
  var pagination = document.querySelector('[data-coll-pagination]');
  if (!input || !panel || !results) return;

  var HANDLES = (root.getAttribute('data-handles') || '')
    .split(',')
    .map(function (h) { return h.trim(); })
    .filter(Boolean)
    .slice(0, 60);
  var SCOPE_LABEL = root.getAttribute('data-scope-label') || 'this collection';
  var MIN_CHARS = 2;
  var MAX_DROPDOWN = 6;
  var MAX_GRID = 60;
  var PAGE_SIZE = 250;
  var MAX_PAGES = 4;
  var CACHE_TTL = 10 * 60 * 1000;
  var CACHE_KEY = 'collsearch:v1:' + HANDLES.join('|');

  var debounceTimer = null;
  var indexPromise = null;
  var activeQuery = '';
  var cursor = -1;

  /* ---------------- helpers ---------------- */

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(amount) {
    var n = Math.round(parseFloat(amount || 0));
    if (!isFinite(n)) n = 0;
    return '₹' + n.toLocaleString('en-IN');
  }

  function normalize(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  var ROMAN = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9, x: 10, xi: 11, xii: 12 };
  var TO_ROMAN = ['', 'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii'];

  /* "Class X" and "Class 10" should find each other, both in the
     product data and in whatever the shopper typed. */
  function expand(norm) {
    if (!norm) return norm;
    var extra = [];
    norm.split(' ').forEach(function (w) {
      if (ROMAN[w]) extra.push(String(ROMAN[w]));
      var n = parseInt(w, 10);
      if (String(n) === w && n >= 1 && n <= 12) extra.push(TO_ROMAN[n]);
    });
    return extra.length ? norm + ' ' + extra.join(' ') : norm;
  }

  function levenshtein(a, b) {
    var m = a.length, n = b.length, i, j;
    if (!m) return n;
    if (!n) return m;
    var row = [];
    for (j = 0; j <= n; j++) row[j] = j;
    for (i = 1; i <= m; i++) {
      var prev = row[0];
      row[0] = i;
      for (j = 1; j <= n; j++) {
        var tmp = row[j];
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
        prev = tmp;
      }
    }
    return row[n];
  }

  /* ---------------- building the index ---------------- */

  function toEntry(p) {
    var variants = p.variants || [];
    var v = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) { v = variants[i]; break; }
    }
    var first = variants[0] || {};
    var chosen = v || first;
    var img = (p.images && p.images[0] && p.images[0].src) || '';
    if (img) img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=400';
    var tags = p.tags || [];
    var hay = expand(normalize([p.title, p.vendor, p.product_type, tags.join(' ')].join(' ')));
    return {
      id: p.id,
      title: p.title,
      url: '/products/' + p.handle,
      img: img,
      price: chosen.price,
      compare: chosen.compare_at_price,
      available: !!v,
      vid: chosen.id,
      hay: hay,
      words: hay.split(' '),
      ntitle: normalize(p.title)
    };
  }

  function fetchCollection(handle) {
    var out = [];
    function page(n) {
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PAGE_SIZE + '&page=' + n, {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          var list = data.products || [];
          out = out.concat(list);
          if (list.length === PAGE_SIZE && n < MAX_PAGES) return page(n + 1);
          return out;
        })
        .catch(function () { return out; });
    }
    return page(1);
  }

  /* Fetch in small batches so a deep category with a couple of dozen
     sub-collections doesn't fire them all at once. */
  function fetchAll() {
    var seen = {};
    var entries = [];
    var queue = HANDLES.slice();

    function batch() {
      if (!queue.length) return Promise.resolve(entries);
      var chunk = queue.splice(0, 6);
      return Promise.all(chunk.map(function (h) {
        return fetchCollection(h).then(function (products) {
          products.forEach(function (p) {
            if (seen[p.id]) return;
            seen[p.id] = true;
            entries.push(toEntry(p));
          });
        });
      })).then(batch);
    }
    return batch();
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.t > CACHE_TTL) return null;
      return parsed.d;
    } catch (e) { return null; }
  }

  function writeCache(entries) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: entries }));
    } catch (e) { /* private mode / quota — the index just rebuilds next visit */ }
  }

  function ensureIndex() {
    if (indexPromise) return indexPromise;
    var cached = readCache();
    if (cached && cached.length) {
      indexPromise = Promise.resolve(cached);
      return indexPromise;
    }
    indexPromise = fetchAll().then(function (entries) {
      writeCache(entries);
      return entries;
    });
    return indexPromise;
  }

  /* ---------------- matching ---------------- */

  function tokenScore(token, entry) {
    if (entry.hay.indexOf(token) !== -1) {
      for (var i = 0; i < entry.words.length; i++) {
        if (entry.words[i].indexOf(token) === 0) return 0;
      }
      return 1;
    }
    if (token.length < 4) return Infinity;
    var thr = token.length <= 6 ? 1 : 2;
    for (var j = 0; j < entry.words.length; j++) {
      var w = entry.words[j];
      if (Math.abs(w.length - token.length) > thr) continue;
      if (levenshtein(token, w) <= thr) return 2;
    }
    return Infinity;
  }

  function search(query, entries) {
    var nq = normalize(query);
    if (!nq) return [];
    var tokens = expand(nq).split(' ').filter(Boolean);
    var scored = [];

    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      var total = 0;
      var ok = true;
      for (var t = 0; t < tokens.length; t++) {
        var s = tokenScore(tokens[t], e);
        if (s === Infinity) { ok = false; break; }
        total += s;
      }
      if (!ok) continue;
      if (e.ntitle.indexOf(nq) === 0) total -= 3;
      else if (e.ntitle.indexOf(nq) !== -1) total -= 1;
      if (!e.available) total += 0.5;
      scored.push({ e: e, s: total, i: i });
    }

    scored.sort(function (a, b) { return a.s - b.s || a.i - b.i; });
    return scored.map(function (x) { return x.e; });
  }

  /* ---------------- rendering ---------------- */

  function cardHtml(p) {
    var hasCompare = p.compare && parseFloat(p.compare) > parseFloat(p.price);
    var pct = hasCompare
      ? Math.round((parseFloat(p.compare) - parseFloat(p.price)) * 100 / parseFloat(p.compare))
      : 0;
    return '' +
      '<div class="p-card" data-product-id="' + escapeHtml(p.id) + '">' +
        '<a href="' + escapeHtml(p.url) + '" class="p-card__media">' +
          (hasCompare && pct > 0 ? '<span class="p-card__discount">' + pct + '% OFF</span>' : '') +
          '<span class="p-card__ribbon">30 MIN</span>' +
          (p.img
            ? '<img src="' + escapeHtml(p.img) + '" alt="" width="200" height="200" loading="lazy">'
            : '<div class="p-card__media-placeholder"></div>') +
        '</a>' +
        '<div class="p-card__body">' +
          '<a href="' + escapeHtml(p.url) + '" class="p-card__title">' + escapeHtml(p.title) + '</a>' +
          '<div class="p-card__price-row">' +
            '<span class="p-card__price">' + money(p.price) + '</span>' +
            (hasCompare ? '<span class="p-card__compare">' + money(p.compare) + '</span>' : '') +
          '</div>' +
          '<div class="p-card__cart" data-cart-control data-variant-id="' + escapeHtml(p.vid) + '">' +
            '<button type="button" class="p-card__add" data-add' + (p.available ? '' : ' disabled') + '>' +
              (p.available ? 'ADD' : 'SOLD OUT') +
            '</button>' +
            (p.available
              ? '<div class="p-card__stepper" data-stepper hidden>' +
                  '<button type="button" class="p-card__step" data-step="-1" aria-label="Decrease quantity">&minus;</button>' +
                  '<span class="p-card__qty" data-qty>0</span>' +
                  '<button type="button" class="p-card__step" data-step="1" aria-label="Increase quantity">+</button>' +
                '</div>'
              : '') +
          '</div>' +
        '</div>' +
      '</div>';
  }

  /* Newly rendered cards need their +/- steppers to reflect what is
     already in the cart — theme.js only syncs the cards that existed
     when it last saw a cart response. */
  function syncCards(container) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var qty = {};
        (cart.items || []).forEach(function (item) {
          var k = String(item.variant_id);
          qty[k] = (qty[k] || 0) + item.quantity;
        });
        Array.prototype.forEach.call(container.querySelectorAll('[data-cart-control]'), function (ctrl) {
          var id = ctrl.getAttribute('data-variant-id');
          var n = (id && qty[id]) || 0;
          var addBtn = ctrl.querySelector('[data-add]');
          var stepper = ctrl.querySelector('[data-stepper]');
          if (!addBtn || !stepper) return;
          if (n > 0) {
            addBtn.hidden = true;
            stepper.hidden = false;
            var qtyEl = stepper.querySelector('[data-qty]');
            if (qtyEl) qtyEl.textContent = n;
          }
        });
      })
      .catch(function () {});
  }

  function renderPanelMessage(msg) {
    panel.innerHTML = '<div class="coll-search__msg">' + msg + '</div>';
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function renderPanel(query, matches) {
    cursor = -1;
    if (!matches.length) {
      renderPanelMessage('No matches for &ldquo;' + escapeHtml(query) + '&rdquo; in ' + escapeHtml(SCOPE_LABEL));
      return;
    }
    var html = '';
    matches.slice(0, MAX_DROPDOWN).forEach(function (p) {
      var hasCompare = p.compare && parseFloat(p.compare) > parseFloat(p.price);
      html +=
        '<a href="' + escapeHtml(p.url) + '" class="coll-search__item" role="option">' +
          '<span class="coll-search__thumb">' +
            (p.img ? '<img src="' + escapeHtml(p.img) + '" alt="">' : '') +
          '</span>' +
          '<span class="coll-search__info">' +
            '<span class="coll-search__title">' + escapeHtml(p.title) + '</span>' +
            '<span class="coll-search__price">' + money(p.price) +
              (hasCompare ? '<span class="coll-search__compare">' + money(p.compare) + '</span>' : '') +
            '</span>' +
          '</span>' +
        '</a>';
    });
    if (matches.length > MAX_DROPDOWN) {
      html += '<button type="button" class="coll-search__footer" data-coll-search-showall>' +
        'Show all ' + matches.length + ' matches below &darr;</button>';
    }
    panel.innerHTML = html;
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function closePanel() {
    panel.hidden = true;
    cursor = -1;
    input.setAttribute('aria-expanded', 'false');
  }

  function renderGrid(query, matches) {
    var shown = matches.slice(0, MAX_GRID);
    /* Unhide BEFORE filling: images parsed inside a display:none
       container can sit there unfetched, leaving blank cards. */
    results.hidden = false;
    results.innerHTML = shown.map(cardHtml).join('');
    if (grid) grid.hidden = true;
    if (pagination) pagination.hidden = true;

    if (empty) empty.hidden = matches.length > 0;
    if (empty && !matches.length) {
      empty.innerHTML = 'Nothing in <strong>' + escapeHtml(SCOPE_LABEL) + '</strong> matches &ldquo;' +
        escapeHtml(query) + '&rdquo;.<br>' +
        '<a href="/search?type=product&q=' + encodeURIComponent(query) + '">Search the whole store instead &rarr;</a>';
    }

    if (status && statusText) {
      status.hidden = false;
      var label = matches.length === 1 ? '1 match' : matches.length + ' matches';
      statusText.innerHTML = matches.length
        ? label + ' for <strong>' + escapeHtml(query) + '</strong> in ' + escapeHtml(SCOPE_LABEL) +
          (matches.length > MAX_GRID ? ' &middot; showing first ' + MAX_GRID : '')
        : 'No matches for <strong>' + escapeHtml(query) + '</strong> in ' + escapeHtml(SCOPE_LABEL);
    }

    if (shown.length) syncCards(results);
  }

  function restore() {
    activeQuery = '';
    results.hidden = true;
    results.innerHTML = '';
    if (grid) grid.hidden = false;
    if (pagination) pagination.hidden = false;
    if (empty) empty.hidden = true;
    if (status) status.hidden = true;
    closePanel();
    setUrlQuery('');
  }

  function setUrlQuery(q) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      if (q) url.searchParams.set('q', q);
      else url.searchParams.delete('q');
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* older browsers — the filter still works, it just isn't linkable */ }
  }

  /* ---------------- driving it ---------------- */

  function run(query) {
    activeQuery = query;
    renderPanelMessage('Searching&hellip;');
    ensureIndex().then(function (entries) {
      if (activeQuery !== query) return;
      if (!entries.length) {
        renderPanelMessage('Nothing to search here yet');
        return;
      }
      var matches = search(query, entries);
      renderPanel(query, matches);
      renderGrid(query, matches);
      setUrlQuery(query);
    });
  }

  input.addEventListener('input', function () {
    var query = input.value.trim();
    clearBtn.hidden = !query;
    clearTimeout(debounceTimer);
    if (query.length < MIN_CHARS) {
      restore();
      return;
    }
    debounceTimer = setTimeout(function () { run(query); }, 160);
  });

  /* Warm the index on first focus so the first keystroke feels instant. */
  input.addEventListener('focus', function () {
    ensureIndex();
    if (!panel.hidden || !panel.innerHTML) return;
    if (input.value.trim().length >= MIN_CHARS) {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
  });

  clearBtn.addEventListener('click', function () {
    input.value = '';
    clearBtn.hidden = true;
    restore();
    input.focus();
  });

  if (resetBtn) {
    resetBtn.addEventListener('click', function () {
      input.value = '';
      clearBtn.hidden = true;
      restore();
    });
  }

  panel.addEventListener('click', function (e) {
    if (e.target.closest('[data-coll-search-showall]')) {
      closePanel();
      if (results && results.scrollIntoView) {
        results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });

  input.addEventListener('keydown', function (e) {
    var items = panel.hidden ? [] : panel.querySelectorAll('.coll-search__item');
    if (e.key === 'Escape') {
      if (!panel.hidden) { closePanel(); return; }
      input.value = '';
      clearBtn.hidden = true;
      restore();
      return;
    }
    if (e.key === 'Enter') {
      if (cursor > -1 && items[cursor]) {
        e.preventDefault();
        window.location.href = items[cursor].getAttribute('href');
        return;
      }
      e.preventDefault();
      closePanel();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (!items.length) return;
    e.preventDefault();
    if (cursor > -1 && items[cursor]) items[cursor].classList.remove('is-active');
    cursor = e.key === 'ArrowDown'
      ? (cursor + 1) % items.length
      : (cursor <= 0 ? items.length - 1 : cursor - 1);
    items[cursor].classList.add('is-active');
    items[cursor].scrollIntoView({ block: 'nearest' });
  });

  document.addEventListener('click', function (e) {
    if (!root.contains(e.target)) closePanel();
  });

  /* Deep link: /collections/iit-jee-books?q=physics lands pre-filtered. */
  (function initFromUrl() {
    var q;
    try { q = new URL(window.location.href).searchParams.get('q'); } catch (e) { q = null; }
    if (!q) return;
    q = q.trim();
    if (q.length < MIN_CHARS) return;
    input.value = q;
    clearBtn.hidden = false;
    ensureIndex().then(function (entries) {
      renderGrid(q, search(q, entries));
    });
  })();
})();